import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import { io, type Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/auth';
import type { Execution } from '@/types';

function socketBaseUrl(): string {
  if (import.meta.env.DEV) {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
  const base = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
  return base || (typeof window !== 'undefined' ? window.location.origin : '');
}

/**
 * Singleton Socket.IO client for execution progress (mirrors backend /socket.io).
 * Uses JWT from the auth store on each handshake / reconnect.
 */
export const useExecutionRealtimeStore = defineStore('executionRealtime', () => {
  const socket = shallowRef<Socket | null>(null);
  const connectionState = ref<'disconnected' | 'connecting' | 'connected'>('disconnected');

  const listeners = new Map<string, Set<(ex: Execution) => void>>();
  const subscriptionRefCounts = new Map<string, number>();

  let connectInFlight: Promise<void> | null = null;

  function dispatchNotificationsRefresh() {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('notifications-updated'));
    }
  }

  function dispatchExecutionUpdate(execution: Execution) {
    const raw = (execution as { _id?: unknown })._id;
    const id = raw != null ? String(raw) : '';
    if (!id) return;
    const set = listeners.get(id);
    if (!set?.size) return;
    for (const cb of set) {
      try {
        cb(execution);
      } catch {
        /* ignore subscriber errors */
      }
    }
  }

  function createSocketIfNeeded(): Socket {
    const existing = socket.value;
    if (existing) return existing;

    const s = io(socketBaseUrl(), {
      path: '/socket.io',
      autoConnect: false,
      transports: ['websocket', 'polling'],
      auth: (cb) => {
        cb({ token: useAuthStore().token ?? '' });
      },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10_000,
    });

    s.on('execution:updated', dispatchExecutionUpdate);
    s.on('notifications:refresh', dispatchNotificationsRefresh);

    s.on('connect', () => {
      connectionState.value = 'connected';
      for (const [id, count] of subscriptionRefCounts) {
        if (count > 0) {
          s.emit('subscribe:execution', id);
        }
      }
    });

    s.on('disconnect', () => {
      connectionState.value = 'disconnected';
    });

    socket.value = s;
    return s;
  }

  async function ensureConnected(): Promise<void> {
    const authStore = useAuthStore();
    if (!authStore.token) {
      return;
    }

    const s = createSocketIfNeeded();
    if (s.connected) {
      connectionState.value = 'connected';
      return;
    }

    if (connectInFlight) {
      return connectInFlight;
    }

    connectionState.value = 'connecting';
    connectInFlight = new Promise<void>((resolve, reject) => {
      const onConnect = () => {
        s.off('connect_error', onError);
        connectInFlight = null;
        connectionState.value = 'connected';
        resolve();
      };
      const onError = (err: Error) => {
        s.off('connect', onConnect);
        connectInFlight = null;
        connectionState.value = 'disconnected';
        reject(err);
      };
      s.once('connect', onConnect);
      s.once('connect_error', onError);
      s.connect();
    });

    const pending = connectInFlight;
    try {
      await pending;
    } catch {
      /* caller may fall back to HTTP */
    }
  }

  /**
   * Receive full execution payloads for one execution id. Ref-counted; unsubscribes when last listener removes.
   */
  function subscribeExecution(executionId: string, onUpdate: (ex: Execution) => void): () => void {
    const id = executionId;
    if (!listeners.has(id)) listeners.set(id, new Set());
    listeners.get(id)!.add(onUpdate);

    const prev = subscriptionRefCounts.get(id) ?? 0;
    const next = prev + 1;
    subscriptionRefCounts.set(id, next);

    /** Only the first subscriber per execution id should hit the server (join is idempotent; repeats flood logs). */
    const emitSubscribe = () => {
      const s = socket.value;
      if (!s?.connected || (subscriptionRefCounts.get(id) ?? 0) === 0) return;
      if (next === 1) {
        s.emit('subscribe:execution', id);
      }
    };

    emitSubscribe();
    void ensureConnected()
      .then(emitSubscribe)
      .catch(() => {
        /* HTTP fallback elsewhere */
      });

    return () => {
      const set = listeners.get(id);
      if (set) {
        set.delete(onUpdate);
        if (set.size === 0) listeners.delete(id);
      }
      const left = (subscriptionRefCounts.get(id) ?? 1) - 1;
      if (left <= 0) {
        subscriptionRefCounts.delete(id);
        socket.value?.emit('unsubscribe:execution', id);
      } else {
        subscriptionRefCounts.set(id, left);
      }
    };
  }

  function disconnect(): void {
    const s = socket.value;
    if (s) {
      s.off('execution:updated', dispatchExecutionUpdate);
      s.off('notifications:refresh', dispatchNotificationsRefresh);
      s.close();
    }
    socket.value = null;
    connectionState.value = 'disconnected';
    listeners.clear();
    subscriptionRefCounts.clear();
    connectInFlight = null;
  }

  return {
    socket,
    connectionState,
    ensureConnected,
    subscribeExecution,
    disconnect,
  };
});
